import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Text,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { FeatureFlagGuard } from './feature-flag-guard';

/**
 * Empty state shown when AI is not enabled.
 * Shows a settings link if the user has permission to edit workspace settings.
 */
export function AiNotEnabledFallback() {
  const { hasPermission } = usePermissions();
  const canEditSettings = hasPermission('account/edit') === true;

  return (
    <Box display="flex" alignItems="center" justifyContent="center" h="full" w="full">
      <EmptyState>
        <EmptyStateTitle>AI is not enabled</EmptyStateTitle>
        <EmptyStateDescription>AI features are not enabled for this workspace.</EmptyStateDescription>
        <EmptyStateActions>
          {canEditSettings ? (
            <Button variant="primary" href="/settings/privacy">
              Go to AI settings
            </Button>
          ) : (
            <Text size="s" color="subtle">
              Ask your account admin to enable AI in settings.
            </Text>
          )}
        </EmptyStateActions>
      </EmptyState>
    </Box>
  );
}

/**
 * Renders children only when the `ai_enabled` feature flag is on.
 * Renders nothing by default when AI is disabled — pass a fallback to override.
 */
export function AiEnabledGuard({ fallback, children }: { fallback?: React.ComponentType; children: React.ReactNode }) {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.AiEnabled} fallback={fallback}>
      {children}
    </FeatureFlagGuard>
  );
}
